/* SPEC-489 — change my password.
 *
 * Used in two places: the dialog behind "Change password" in the rail account menu, and
 * the full-screen gate a user sees after an admin reset (must_change_password). The server
 * ends every older session on success and returns a fresh one, which this form stores via
 * `applySession`, so the user stays signed in on this tab.
 *
 * Errors are the server's own words (wrong current password, policy, rate limit); the
 * client-side checks only name a problem before the round trip. */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, type User } from '@/lib/auth';
import { request } from '@/lib/api';
import { passwordProblems, MIN_LENGTH } from '@/lib/passwordPolicy';

const INPUT =
  'w-full rounded-lg border border-[var(--border-default)] bg-[var(--bg-overlay)] px-3 py-2 text-sm ' +
  'text-[var(--text-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60';

interface Props {
  /** True on the forced screen: the current password is the one-time password. */
  forced?: boolean;
  onDone?: () => void;
  onCancel?: () => void;
}

export function ChangePasswordForm({ forced = false, onDone, onCancel }: Props) {
  const { applySession } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstRef.current?.focus();
  }, []);

  const problems = passwordProblems(next, confirm, current);
  const canSubmit = current.length > 0 && problems.length === 0 && !busy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await request<{ access_token: string; user: User }>('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: current, new_password: next }),
      });
      applySession(res.access_token, res.user);
      setCurrent('');
      setNext('');
      setConfirm('');
      onDone?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The password was not changed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3" noValidate>
      <div>
        <label htmlFor="pw-current" className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
          {forced ? 'One-time password' : 'Current password'}
        </label>
        <input
          id="pw-current"
          ref={firstRef}
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className={INPUT}
        />
      </div>
      <div>
        <label htmlFor="pw-new" className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
          New password
        </label>
        <input
          id="pw-new"
          type="password"
          autoComplete="new-password"
          aria-describedby="pw-rules"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className={INPUT}
        />
        <p id="pw-rules" className="mt-1 text-[11px] text-[var(--text-tertiary)]">
          At least {MIN_LENGTH} characters, with one uppercase letter and one number.
        </p>
      </div>
      <div>
        <label htmlFor="pw-confirm" className="block text-xs font-medium text-[var(--text-secondary)] mb-1">
          New password again
        </label>
        <input
          id="pw-confirm"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={INPUT}
        />
      </div>

      {touched && problems.length > 0 && (
        <ul className="text-xs text-amber-400 list-disc pl-4 space-y-0.5">
          {problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}
      <p className="text-[11px] text-[var(--text-tertiary)]">
        Changing it signs you out everywhere else. This tab stays signed in.
      </p>

      <div className="flex items-center justify-end gap-2 pt-1">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={busy}
          className="px-3 py-2 rounded-lg text-sm font-medium bg-amber-500 text-black hover:bg-amber-400 disabled:cursor-wait focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60"
        >
          {busy ? 'Changing…' : 'Change password'}
        </button>
      </div>
    </form>
  );
}

/** Modal wrapper for the account-menu entry. Escape or the backdrop closes it. Portalled to
 *  <body> so the rail's own stacking context cannot clip or cover it. */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
        className="relative w-full max-w-sm bg-[var(--bg-raised)] border border-[var(--border-subtle)] rounded-2xl shadow-2xl p-5"
      >
        <h2 id="change-password-title" className="text-base font-display font-bold text-[var(--text-primary)] mb-3">
          Change password
        </h2>
        {done ? (
          <div className="space-y-3">
            <p role="status" className="text-sm text-[var(--text-secondary)]">
              Password changed. Other devices are signed out.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 rounded-lg text-sm font-medium bg-amber-500 text-black hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <ChangePasswordForm onDone={() => setDone(true)} onCancel={onClose} />
        )}
      </div>
    </div>,
    document.body,
  );
}

/** The full-screen gate after an admin reset. Nothing else in the app loads (the server
 *  answers 403 to every other route) until the one-time password is replaced. */
export function ForcedPasswordChange() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-[var(--bg-raised)] border border-[var(--border-subtle)] rounded-2xl shadow-2xl p-6">
        <h1 className="text-lg font-display font-bold text-[var(--text-primary)]">Choose a new password</h1>
        <p className="mt-1 mb-4 text-sm text-[var(--text-secondary)]">
          {user?.full_name || user?.username}, your password was reset by an administrator. Enter the
          one-time password you were given, then choose your own.
        </p>
        <ChangePasswordForm forced />
        <button
          type="button"
          onClick={logout}
          className="mt-3 text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}
